import { useEffect } from "react";
import { VIEW_MODE_EVENT, getViewRunState } from "./viewMode";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Subscribe to the global Live/Replay switch. `cb(live, isInitialSync)` is called
 *  once on mount with the current mode (`isInitialSync = true`), then again with
 *  `isInitialSync = false` whenever the mode actually changes. Returns an
 *  unsubscribe fn. This is the imperative core; `useFollowViewMode` wraps it for
 *  React components, and DiagnosticsWindow / TigerViewPanel / MenuBar call it
 *  directly from their own effects.
 *
 *  Desktop uses the Tauri `view-mode-changed` event. The web build has NO event
 *  channel, so it can't rely on the menu-bar switch's one-shot
 *  `BroadcastChannel("tiger-view-mode")` ping alone: a tool window opened as a
 *  separate browser tab is backgrounded when you toggle in the main interface, and
 *  browsers freeze/throttle background tabs — the ping fires once and is lost, and
 *  a mount-only sync never re-checks, so the tool stays in the old mode ("doesn't
 *  react at all"). So the web path POLLS `get_view_run_state` (1s) as the reliable
 *  backstop — the same cadence the replay toolbar already polls at, and it doubles
 *  as the session-liveness heartbeat the idle reaper needs — and ALSO re-syncs on
 *  the BroadcastChannel ping and on the tab regaining visibility for instant
 *  response. It fires only when THIS window's own session mode changes.
 *
 *  ⚠️ Run state is PER-SESSION on the webserver (each `?live=<config>` tab is its
 *  own backend session, scoped via the X-Tiger-Tab header), so the web path always
 *  RE-READS `getViewRunState()` rather than trusting the unscoped broadcast `{live}`
 *  payload — a toggle on another turbine's tab must not flip this window. */
export function onViewModeChange(
  cb: (live: boolean, isInitialSync?: boolean) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  let stopped = false;

  if (isTauri()) {
    let off: (() => void) | null = null;
    void (async () => {
      const initial = await getViewRunState();
      if (stopped) return;
      cb(initial === "live", true); // on-mount sync — not a user switch
      const { listen } = await import("@tauri-apps/api/event");
      const fn = await listen<{ live: boolean }>(VIEW_MODE_EVENT, (ev) =>
        cb(ev.payload.live, false),
      );
      if (stopped) fn();
      else off = fn;
    })();
    return () => { stopped = true; if (off) off(); };
  }

  // Web: poll + ping + visibility, firing only on a genuine per-session change.
  // The first resolved read is the initial sync (isInitialSync = true). Reads
  // resolve sequentially on the single JS thread, so a poll/ping/visibility read
  // racing the initial one can't double-fire: whichever runs first sets `prev`.
  let prev: boolean | null = null;
  const check = async () => {
    const live = (await getViewRunState()) === "live";
    if (stopped) return;
    const isInitial = prev === null;
    if (isInitial || live !== prev) {
      prev = live;
      cb(live, isInitial);
    }
  };
  void check(); // mount sync
  const id = setInterval(() => void check(), 1000);
  const ch =
    typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("tiger-view-mode") : null;
  if (ch) ch.onmessage = () => void check();
  const onVisible = () => {
    if (!document.hidden) void check();
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    stopped = true;
    clearInterval(id);
    if (ch) ch.close();
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/** React-hook wrapper around `onViewModeChange` for tool windows. `onLive(live)`
 *  is called on mount with the current mode and again whenever it changes, so a
 *  window opened while already in replay starts paused rather than polling live. */
export function useFollowViewMode(
  onLive: (live: boolean, isInitialSync?: boolean) => void,
): void {
  useEffect(() => onViewModeChange(onLive), [onLive]);
}
